import { useCalculateCompliance } from "@features/ax/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader } from "@shared/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/ui/dialog";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { Calculator } from "lucide-react";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";

export const OverviewLanding: React.FC<{ data: any }> = ({ data }) => {
  const remoteUtilityId = getRemoteUtilityId();
  const { id } = useParams();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [complianceResponse, setComplianceResponse] = useState<any>(null);

  const calculateComplianceMutation = useCalculateCompliance();
  const networkType = 5;

  const handleCalculateCompliance = () => {
    const payload = {
      entity_type: networkType,
      entity_id: id,
      remote_utility_id: remoteUtilityId,
    };

    calculateComplianceMutation.mutate(payload, {
      onSuccess: (responseData) => {
        setComplianceResponse(responseData);
        setIsDialogOpen(true);
      },
      onError: (error) => {
        console.error("Error calculating compliance:", error);
      },
    });
  };

  // Transform API response data for monthly averages
  const monthlyAverageData = useMemo(() => {
    if (!complianceResponse?.monthlyAverages) return [];

    return Object.entries(complianceResponse.monthlyAverages).map(
      ([parameter, data]: [string, any]) => {
        // Get status from parameters section
        const parameterStatus =
          complianceResponse?.parameters?.[parameter]?.status || "Unknown";

        return {
          parameter,
          averageValue: data.average,
          unit: data.unit,
          guideline: `${data.fieldMinLimit} - ${data.fieldMaxLimit} ${data.unit}`,
          status: parameterStatus,
        };
      }
    );
  }, [complianceResponse]);

  // Transform API response data for violations
  const violationsData = useMemo(() => {
    if (!complianceResponse?.parameters) return [];

    const violationsList: any[] = [];
    Object.entries(complianceResponse.parameters).forEach(
      ([parameter, paramData]: [string, any]) => {
        if (paramData.violations?.length > 0) {
          paramData.violations.forEach((violation: any) => {
            violationsList.push({
              parameter,
              value: `${violation.valueRecorded} ${paramData.unit}`,
              guideline: `${paramData.minLimit} - ${paramData.maxLimit} ${paramData.unit}`,
              date: violation.date,
              severity: violation.severity.toLowerCase(),
            });
          });
        }
      }
    );
    return violationsList;
  }, [complianceResponse]);

  const getStatusBadge = (status: string) => {
    if (!status) return null;

    const getStatusColor = (status: string) => {
      const normalizedStatus = status.toLowerCase();
      switch (normalizedStatus) {
        case "compliant":
        case "in compliance":
        case "on schedule":
          return "bg-green-100 text-green-800";
        case "violation":
        case "exceeded":
        case "exceedance":
          return "bg-red-100 text-red-800";
        case "due-soon":
        case "upcoming":
        case "pending":
          return "bg-yellow-100 text-yellow-800";
        default:
          return "bg-gray-100 text-gray-800";
      }
    };

    return (
      <Badge className={getStatusColor(status)} variant="secondary">
        {status}
      </Badge>
    );
  };

  const getSeverityBadge = (severity: string) => {
    const getSeverityColor = (severity: string) => {
      switch (severity) {
        case "high":
          return "bg-red-100 text-red-800";
        case "moderate":
          return "bg-yellow-100 text-yellow-800";
        case "low":
          return "bg-green-100 text-green-800";
        default:
          return "bg-gray-100 text-gray-800";
      }
    };

    return (
      <Badge className={getSeverityColor(severity)} variant="secondary">
        {severity}
      </Badge>
    );
  };

  const upcomingParametersColumns: TableColumn<any>[] = [
    {
      key: "parameter",
      header: "Parameter",
      type: "text",
      render: (value) => <span className="font-medium">{value}</span>,
    },
    {
      key: "frequency",
      header: "Frequency",
      type: "text",
    },
    {
      key: "nextDueDate",
      header: "Next Due Date",
      type: "text",
    },
    {
      key: "status",
      header: "Status",
      type: "custom",
      render: (value) => getStatusBadge(value),
    },
  ];

  const monthlyAverageColumns: TableColumn<any>[] = [
    {
      key: "parameter",
      header: "Parameter",
      type: "text",
      render: (value) => <span className="font-medium">{value}</span>,
    },
    {
      key: "averageValue",
      header: "Monthly Average",
      type: "text",
      render: (value, row) => `${value} ${row.unit}`,
    },
    {
      key: "guideline",
      header: "Range",
      type: "text",
    },
    {
      key: "status",
      header: "Status",
      type: "custom",
      render: (value) => getStatusBadge(value),
    },
  ];

  const violationsColumns: TableColumn<any>[] = [
    {
      key: "parameter",
      header: "Parameter",
      type: "text",
      render: (value) => <span className="font-medium">{value}</span>,
    },
    {
      key: "value",
      header: "Value Recorded",
      type: "text",
      render: (value) => (
        <span className="text-red-600 font-medium">{value}</span>
      ),
    },
    {
      key: "guideline",
      header: "Range",
      type: "text",
    },
    {
      key: "date",
      header: "Date of Violation",
      type: "text",
    },
    {
      key: "severity",
      header: "Severity",
      type: "custom",
      render: (value) => getSeverityBadge(value),
    },
  ];

  const latestReadingsColumns: TableColumn<any>[] = [
    {
      key: "parameter",
      header: "Parameter",
      type: "text",
      sortable: true,
    },
    {
      key: "lastReading",
      header: "Last Reading",
      type: "text",
    },
    {
      key: "sndwsGuideline",
      header: "Range",
      type: "text",
    },
    {
      key: "status",
      header: "Status",
      type: "custom",
      render: (value: string) => getStatusBadge(value),
    },
  ];

  const createPagination = (dataLength: number) => ({
    currentPage: 1,
    totalPages: 1,
    totalItems: dataLength || 0,
    itemsPerPage: dataLength || 0,
    startItem: 1,
    endItem: dataLength || 0,
  });

  return (
    <div>
      <div className="flex justify-end mb-6">
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleCalculateCompliance}>
              <Calculator className="h-4 w-4 mr-2" />
              Calculate Monthly Compliance
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Monthly Compliance Report</DialogTitle>
              <DialogDescription>
                Monthly Compliance Report -{" "}
                {new Date().toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <p className="page-title">
                    Monthly Average Quality Parameters
                  </p>
                  <p className="page-subtitle">
                    Averaged values for all monitored parameters this month
                  </p>
                </CardHeader>
                <CardContent className="p-0 mt-3">
                  <GenericTable
                    data={monthlyAverageData}
                    columns={monthlyAverageColumns}
                    rowKey="parameter"
                    pagination={createPagination(monthlyAverageData.length)}
                    emptyMessage="No monthly average parameters found"
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <p className="page-title">Violations</p>
                  <p className="page-subtitle">
                    Parameters that exceeded guidelines during the monitoring
                    period
                  </p>
                </CardHeader>
                <CardContent className="p-0 mt-3">
                  <GenericTable
                    data={violationsData}
                    columns={violationsColumns}
                    rowKey="parameter"
                    pagination={createPagination(violationsData.length)}
                    emptyMessage="No violations found"
                  />
                </CardContent>
              </Card>
            </div>
            <DialogFooter className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <p className="page-title">Upcoming Parameter Monitoring</p>
          <p className="page-subtitle">Next scheduled sampling activities</p>
        </CardHeader>
        <CardContent className="p-0 mt-3">
          <GenericTable
            data={data?.upcoming || []}
            columns={upcomingParametersColumns}
            rowKey="upcomingMonitoring"
            pagination={createPagination(data?.upcoming?.length)}
            emptyMessage="No upcoming parameter monitoring found"
          />
        </CardContent>
      </Card>

      <Card className="mt-3">
        <CardHeader>
          <p className="page-title">Latest Parameter Readings</p>
          <p className="page-subtitle">
            Most recent water quality test results
          </p>
        </CardHeader>
        <CardContent>
          <GenericTable
            data={data?.latest || []}
            columns={latestReadingsColumns}
            rowKey="parameter"
            emptyMessage="No parameter readings found"
            pagination={createPagination(data?.latest?.length)}
          />
        </CardContent>
      </Card>
    </div>
  );
};
